import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiEndpoints, apiRequest, resolveApiAssetUrl } from '@/config/api';
import { BlogPost as BlogPostType, ApiResponse } from '@/types/api';

const BlogSection = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['blog-section'],
    queryFn: () => apiRequest<ApiResponse<BlogPostType[]>>(`${apiEndpoints.blogs}?limit=3`),
  });
  const posts = data?.data || [];

  return (
    <section id="blog" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            آخرین مطالب بلاگ
          </h2>
          <p className="text-steel dark:text-slate-300 text-lg max-w-2xl mx-auto">
            آخرین اخبار، مقالات و راهنماهای تخصصی حوزه الکترو موتورهای الکتریکی
          </p>
        </motion.div>

        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-copper" /></div>
        ) : posts.length === 0 ? (
          <p className="text-center text-muted-foreground">هنوز مقاله‌ای منتشر نشده است.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-card rounded-lg border border-border shadow-lg overflow-hidden hover-lift group"
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="relative h-48 bg-gradient-to-br from-copper/10 to-steel/10 flex items-center justify-center overflow-hidden">
                      {post.featured_image ? (
                        <img src={resolveApiAssetUrl(post.featured_image) || ''} alt={post.title} width={600} height={400} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" decoding="async" />
                      ) : (
                        <div className="text-6xl opacity-60">⚡</div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center text-sm text-muted-foreground mb-3"><Calendar className="w-4 h-4 ml-2" />{new Date(post.publish_date || post.created_at).toLocaleDateString('fa-IR')}</div>
                      <h3 className="text-xl font-bold text-steel-dark mb-3 group-hover:text-copper transition-colors line-clamp-2">{post.title}</h3>
                      <p className="text-muted-foreground leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                      <div className="flex items-center text-copper font-medium"><span className="ml-2">ادامه مطلب</span><ArrowLeft className="w-4 h-4" /></div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
            <div className="text-center mt-8">
              <Button asChild className="bg-copper hover:bg-copper-dark text-white px-8">
                <Link to="/blog">مشاهده تمام مطالب</Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default BlogSection;
